import React, { useState } from "react";
import { ChevronDown, ChevronRight, Loader2, Layers3 } from "lucide-react";
import { api } from "@/lib/api";
import { ProvenanceBadge } from "@/components/common/Badges";

// Module-scoped cache keyed on the sorted function-name list — the same
// conflict pair renders in many places, fetch its definition once.
const fnCache = new Map();

/** One function's full drill-down: Actions (T-codes), Authorization Objects
 * and captured Permission field values. `matched` T-codes (the ones this
 * role actually holds) glow accent. */
export function FunctionDetailBlock({ fn, matched = [] }) {
    const matchedSet = new Set((matched || []).map((t) => t.toUpperCase()));
    const objectFields = fn.object_fields || {};
    const objectSources = fn.object_sources || {};
    return (
        <div className="panel p-2.5 rounded-[4px] border border-white/5">
            <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[10px] text-cyan-300">{fn.function_id}</span>
                <span className="text-[11px] text-white font-medium">{fn.name}</span>
                {fn.bp && <span className="text-[9px] font-mono uppercase tracking-wider text-slate-500">{fn.bp}</span>}
                <ProvenanceBadge
                    provenance={fn.provenance}
                    enrichment={fn.enrichment}
                    testId={`function-provenance-${fn.function_id}`}
                />
            </div>
            {fn.provenance === "utsava_enriched" && fn.enrichment?.basis && (
                <p
                    data-testid={`function-enrichment-basis-${fn.function_id}`}
                    className="mt-1.5 text-[10px] leading-relaxed text-violet-200/80"
                >
                    <span className="font-mono uppercase tracking-wider text-violet-300">Enrichment basis · </span>
                    {fn.enrichment.basis}
                </p>
            )}
            <div className="mt-2">
                <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500 mb-1">
                    Actions · T-codes ({(fn.actions || []).length}){matchedSet.size > 0 ? " · glowing = held by this role" : ""}
                </div>
                <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                    {(fn.actions || []).map((a) => (
                        <span
                            key={a}
                            className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm border ${
                                matchedSet.has(a.toUpperCase())
                                    ? "bg-cyan-500/20 text-cyan-200 border-cyan-400/60 shadow-[0_0_8px_rgb(var(--accent-1-rgb) / 0.35)]"
                                    : "bg-white/5 text-slate-400 border-white/10"
                            }`}
                        >
                            {a}
                        </span>
                    ))}
                    {(fn.actions || []).length === 0 && <span className="text-[10px] font-mono text-slate-400">none captured in the GRC export</span>}
                </div>
            </div>
            <div className="mt-2">
                <div className="text-[9px] font-mono uppercase tracking-wider text-slate-500 mb-1">
                    Permissions · Authorization Objects &amp; field values ({(fn.objects || []).length})
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                    {(fn.objects || []).map((o) => {
                        const fields = objectFields[o];
                        const via = (objectSources[o] || []).join(", ");
                        return (
                            <div key={o} className="flex items-start gap-2 flex-wrap">
                                <span title={via ? `granted via ${via}` : undefined} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-white/5 text-slate-300 border border-white/10 shrink-0">
                                    {o}
                                </span>
                                {fields && Object.entries(fields).map(([field, values]) => (
                                    <span key={field} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                                        {field} = {(values || []).join(", ")}
                                    </span>
                                ))}
                            </div>
                        );
                    })}
                    {(fn.objects || []).length === 0 && <span className="text-[10px] font-mono text-slate-400">none captured in the GRC export</span>}
                </div>
            </div>
        </div>
    );
}

/** Lazy drill-down for a SoD conflict / critical-access hit: expands
 * Risk → Function → Actions (T-codes) → Permissions (auth objects + field
 * values), fetched from the live ruleset the first time it opens.
 * `functions` = [{ name, matched: [tcodes…] }, …]. */
export function FunctionDrilldown({ functions, testId }) {
    const key = functions.map((f) => f.name).sort().join("|");
    const [open, setOpen] = useState(false);
    const [detail, setDetail] = useState(fnCache.get(key) || null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const toggle = async (e) => {
        e.stopPropagation();
        const next = !open;
        setOpen(next);
        if (!next || detail || loading) return;
        setLoading(true);
        setError("");
        try {
            const names = functions.map((f) => f.name).join(",");
            const { data } = await api.get("/roles/function-detail", { params: { names } });
            fnCache.set(key, data.functions);
            setDetail(data.functions);
        } catch (err) {
            setError("Drill-down unavailable — could not resolve these functions in the live ruleset");
        } finally {
            setLoading(false);
        }
    };

    const matchedFor = (fnDef) => {
        const hit = functions.find((f) => f.name === fnDef.name || f.name === fnDef.function_id);
        return hit?.matched || [];
    };

    return (
        <div className="mt-2">
            <button
                data-testid={testId}
                onClick={toggle}
                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-cyan-400/90 hover:text-cyan-300 transition-colors"
            >
                {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                <Layers3 size={10} /> Drill down · T-codes, Auth Objects &amp; field values
            </button>
            {open && (
                <div className="mt-2 space-y-2">
                    {loading && (
                        <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500 py-2">
                            <Loader2 size={12} className="animate-spin" /> Resolving functions in the live ruleset…
                        </div>
                    )}
                    {error && !loading && <p className="text-[11px] text-rose-300 font-mono">{error}</p>}
                    {detail && !loading && detail.map((fn) => (
                        <FunctionDetailBlock key={fn.function_id} fn={fn} matched={matchedFor(fn)} />
                    ))}
                    {detail && !loading && detail.length === 0 && (
                        <p className="text-[11px] text-slate-500 font-mono">These functions are no longer present in the live ruleset.</p>
                    )}
                </div>
            )}
        </div>
    );
}
